import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DocusealBuilder } from "@docuseal/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DOCUMENT_SCOPES,
  getDocusealErrorMessage,
  isForbiddenError,
  type BuilderTokenResponse,
  type ContractDocument,
  type ContractKind,
  type DocumentScope,
  type FieldSeedsByKind,
  type RemoteTemplate,
} from "@/services/docuseal";
import { queryKeys } from "@/queryKeys";
import {
  BuilderViewport,
  builderParties,
  configSavePayload,
  DEFAULT_BUILDER_KIND,
  deleteUnavailableReason,
  docusealBuilderFields,
  invalidateDocumentQueries,
  isConfigDirty,
  laneJurisdiction,
  nextSavedName,
  type ConfigForm,
  type SavedConfig,
} from "@/pages/admin/documentPagesShared";
import { useBuilderBridge, useBuilderElement } from "@/pages/admin/useBuilderBridge";
import {
  DOCUSEAL_APP_THEME_CSS,
  DOCUSEAL_BUILDER_TITLE_CSS,
  DOCUSEAL_HIDE_BUILDER_SAVE_CSS,
  DOCUSEAL_HIDE_PARTY_PICKER_CSS,
  DOCUSEAL_VARIABLES_TAB_CSS,
} from "@/pages/admin/documentBuilderTheme";
import DeleteTemplateButton from "@/components/documents/DeleteTemplateButton";
import DocumentConfigCard from "@/components/documents/DocumentConfigCard";
import SaveButton from "@/components/documents/SaveButton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";

/** The builder JWT lives an hour; one is minted per visit and never refetched behind the iframe. */
const TOKEN_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: 0,
  refetchOnWindowFocus: false,
  retry: false,
} as const;

/** Loads everything the editor starts from, then mounts it once. */
const DocumentEdit: React.FC<{ scope?: DocumentScope }> = ({ scope = DOCUMENT_SCOPES.org }) => {
  const { service } = scope;
  const { id } = useParams();
  const queryClient = useQueryClient();
  const templateId = Number(id);
  const validId = Number.isInteger(templateId) && templateId > 0;

  const remoteQuery = useQuery({
    queryKey: queryKeys.contractRemoteTemplate(scope.key, templateId),
    queryFn: () => service.getRemoteTemplate(templateId),
    enabled: validId,
    retry: false,
  });
  const documentsQuery = useQuery({
    queryKey: queryKeys.contractAdminDocuments(scope.key),
    queryFn: () => service.listAdminDocuments(),
  });
  const seedsQuery = useQuery({
    queryKey: queryKeys.contractFieldSeeds(scope.key),
    queryFn: () => service.listFieldSeeds(),
    staleTime: Infinity,
  });
  const tokenKey = queryKeys.contractBuilderToken(scope.key, templateId);
  const tokenQuery = useQuery({
    queryKey: tokenKey,
    queryFn: () => service.mintBuilderToken({ templateId }),
    enabled: remoteQuery.isSuccess,
    ...TOKEN_QUERY_OPTIONS,
  });

  const documents = documentsQuery.data;
  const localDoc = documents?.find((doc) => doc.docuseal_template_id === templateId);
  // An enabled row this org cannot verify (403 on the remote read) still gets its
  // config card so Enable can be turned off, but there is no builder to open.
  const unverified = isForbiddenError(remoteQuery.error) && Boolean(localDoc);

  const loadError = !validId
    ? "Invalid template id."
    : ((unverified ? null : remoteQuery.error) ??
      documentsQuery.error ??
      seedsQuery.error ??
      tokenQuery.error);
  const ready =
    documents && seedsQuery.data && (unverified || (remoteQuery.data && tokenQuery.data));

  return (
    <div className="space-y-6">
      <div>
        <Link to={scope.basePath} className="text-sm text-primary hover:underline">
          ← {scope.title}
        </Link>
      </div>
      {loadError ? (
        <Alert variant="destructive">
          <AlertDescription>
            {typeof loadError === "string" ? loadError : getDocusealErrorMessage(loadError)}
          </AlertDescription>
        </Alert>
      ) : !ready ? (
        <p className="text-sm text-muted-foreground">Minting builder session…</p>
      ) : (
        <DocumentEditor
          // Remount per template. Saving keeps the editor mounted so form state and any
          // error survive; its state already matches the row Save creates.
          key={templateId}
          scope={scope}
          templateId={templateId}
          remoteName={remoteQuery.data?.name ?? localDoc?.name ?? ""}
          documentHint={remoteQuery.data?.document_hint ?? null}
          documents={documents}
          seeds={seedsQuery.data}
          token={unverified ? null : (tokenQuery.data ?? null)}
          remintToken={() => queryClient.refetchQueries({ queryKey: tokenKey })}
        />
      )}
    </div>
  );
};

interface DocumentEditorProps {
  scope: DocumentScope;
  templateId: number;
  remoteName: string;
  /** The server's note for a template the merchant only fills (see `document_hint`). */
  documentHint: string | null;
  documents: ContractDocument[];
  seeds: FieldSeedsByKind;
  /** null when the template cannot be opened (unverified): the card shows, the builder does not. */
  token: BuilderTokenResponse | null;
  remintToken: () => Promise<unknown>;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({
  scope,
  templateId,
  remoteName,
  documentHint,
  documents,
  seeds,
  token,
  remintToken,
}) => {
  const { service } = scope;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const local = documents.find((doc) => doc.docuseal_template_id === templateId) ?? null;
  const [name, setName] = useState(remoteName);
  const [kind, setKind] = useState<ContractKind>(local?.kind ?? DEFAULT_BUILDER_KIND);
  const [jurisdiction, setJurisdiction] = useState<string | null>(local?.jurisdiction ?? null);
  // Like the rest of the form, applied by Save rather than on toggle.
  const [enabled, setEnabled] = useState(local?.is_active ?? false);
  // Before the first Save the builder keeps one stable session, so type edits in the form
  // do not remount it and lose unsaved builder work. Its palette is the type it opened with.
  const [initialKind] = useState(kind);
  // From the edit token (minted after the Funder party is added), fixed for the session:
  // a Save or a reminted token must not change which parties the open builder offers.
  const [parties] = useState(() => builderParties(token?.roles));
  const [error, setError] = useState<string | null>(null);

  // What is saved: the DocuSeal name (the row's follows it) and the row's type and state.
  const saved: SavedConfig = {
    name: remoteName,
    kind: local?.kind ?? DEFAULT_BUILDER_KIND,
    jurisdiction: local?.jurisdiction ?? null,
    is_active: local?.is_active ?? false,
  };
  const displayName = nextSavedName(name, remoteName);
  // An unopenable template can only be turned off, so nothing else in the form is saved.
  const form: ConfigForm = token ? { name, kind, jurisdiction, enabled } : { ...saved, enabled };
  const configDirty = isConfigDirty(saved, form);
  const builderKind = local?.kind ?? initialKind;
  const fields = docusealBuilderFields(seeds[builderKind] ?? []);

  const refresh = () => invalidateDocumentQueries(queryClient);
  const onMutationError = (err: unknown) => setError(getDocusealErrorMessage(err));

  // A token minted before the cleanup points the builder at the pre-PUT template,
  // so the unplaced variables we just removed would still be listed. Remint after the PUT settles.
  const cleanThenRefresh = async (cleaned: boolean) => {
    try {
      if (cleaned) await remintToken();
    } catch (err) {
      onMutationError(err);
    } finally {
      await refresh();
    }
  };

  const [builderHost, setBuilderHost] = useState<HTMLDivElement | null>(null);
  const bridge = useBuilderBridge(useBuilderElement(builderHost), {
    templateId,
    // A template may have no fields (sent as its raw PDF); the builder will not save that.
    clearFields: service.clearTemplateFields,
    // The builder's own Save (reachable in fullscreen) saves the page's settings too.
    onSave: (_template, external) => {
      if (external) saveMutation.mutate({ builder: false });
    },
  });

  /**
   * Save the name, type and on/off state in one call. Resolves true when the template's variables were
   * cleaned up. The name goes with every save: the builder's own save writes its loaded
   * copy of it back, and the server only renames the template when it differs.
   */
  const persistConfig = async (): Promise<boolean> => {
    let cleaned: boolean;
    let doc: ContractDocument;
    if (local) {
      const payload = configSavePayload(local, form);
      // The server checks ownership before renaming, which an unopenable template fails.
      doc = await service.updateDocument(
        local.id,
        token ? { name: displayName, ...payload } : payload
      );
      // `kind` is only sent when it changed, which re-seeded the template's variables.
      cleaned = "kind" in payload;
    } else {
      // The server makes the row the default when it is enabled into an empty lane.
      doc = await service.registerDocument({
        kind,
        name: displayName,
        docuseal_template_id: templateId,
        jurisdiction: laneJurisdiction(kind, jurisdiction),
        is_active: enabled,
        clean_template_fields: true,
      });
      cleaned = true;
    }
    // The row carries the name DocuSeal reports back; the page's cached read follows it.
    // An unopenable template sent no name, and writing its read would clear the 403 that
    // keeps the page on the config card.
    if (token) {
      queryClient.setQueryData<RemoteTemplate>(
        queryKeys.contractRemoteTemplate(scope.key, templateId),
        (current) => ({ ...current, id: templateId, name: doc.name })
      );
    }
    return cleaned;
  };

  const saveMutation = useMutation({
    mutationFn: async ({ builder }: { builder: boolean }) => {
      // The builder first: its save writes its loaded name back, so the name lands last.
      if (builder && bridge.dirty) await bridge.save();
      return persistConfig();
    },
    onSuccess: cleanThenRefresh,
    onError: (err) => {
      onMutationError(err);
      void refresh();
    },
  });

  const makeDefaultMutation = useMutation({
    mutationFn: (docId: number) => service.updateDocument(docId, { is_default: true }),
    onSuccess: refresh,
    onError: onMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => service.deleteRemoteTemplate(templateId),
    onSuccess: async () => {
      await refresh();
      navigate(scope.basePath);
    },
    onError: onMutationError,
  });

  const dirty = bridge.dirty || configDirty;
  const saving = saveMutation.isPending;
  const justSaved = saveMutation.isSuccess;
  const saveDisabled = !dirty || saving;
  const startSave = () => {
    setError(null);
    saveMutation.mutate({ builder: true });
  };

  return (
    <>
      <h1 className="text-3xl font-semibold tracking-tight">{displayName}</h1>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {documentHint && <p className="text-sm text-muted-foreground">{documentHint}</p>}

      <DocumentConfigCard
        name={name}
        onNameChange={setName}
        kind={kind}
        onKindChange={setKind}
        jurisdiction={jurisdiction}
        onJurisdictionChange={setJurisdiction}
        enabled={enabled}
        // An unopenable template cannot be turned on, only off.
        enableDisabled={saving || (!token && !saved.is_active)}
        onEnabledChange={setEnabled}
        // An unopenable template cannot be picked for sends, so it has no default to make.
        isDefault={local?.is_active && token ? local.is_default : null}
        makeDefaultDisabled={Boolean(local?.is_default) || makeDefaultMutation.isPending || saving}
        onMakeDefault={() => {
          setError(null);
          if (local) makeDefaultMutation.mutate(local.id);
        }}
        saveDisabled={saveDisabled}
        dirty={dirty}
        saved={justSaved}
        saving={saving}
        onSave={startSave}
        headerAction={
          <DeleteTemplateButton
            templateName={displayName}
            unavailableReason={deleteUnavailableReason(saved.is_active)}
            busy={deleteMutation.isPending}
            onConfirm={() => {
              setError(null);
              deleteMutation.mutate();
            }}
          />
        }
      />

      {token && (
        <Card>
          <CardContent className="pt-6">
            <BuilderViewport
              variableNames={fields.map((field) => field.name)}
              onHostChange={setBuilderHost}
              // The builder's own Save is hidden, so fullscreen carries the page's.
              fullscreenActions={
                <SaveButton
                  dirty={dirty}
                  saving={saving}
                  saved={justSaved}
                  disabled={saveDisabled}
                  onClick={startSave}
                />
              }
            >
              <DocusealBuilder
                key={`${local ? local.kind : `initial|${initialKind}`}|${token.token}`}
                token={token.token}
                host={token.host}
                fields={fields}
                roles={parties}
                withCustomFieldsTab={fields.length > 0}
                customCss={[
                  DOCUSEAL_APP_THEME_CSS,
                  DOCUSEAL_BUILDER_TITLE_CSS,
                  DOCUSEAL_HIDE_BUILDER_SAVE_CSS,
                  fields.length > 0 ? DOCUSEAL_VARIABLES_TAB_CSS : "",
                  parties ? "" : DOCUSEAL_HIDE_PARTY_PICKER_CSS,
                ]
                  .filter(Boolean)
                  .join("\n")}
                withSendButton={false}
                withSignYourselfButton={false}
                autosave={false}
                withTitle
                style={{ height: "100%", width: "100%" }}
              />
            </BuilderViewport>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DocumentEdit;
